// Review storage is deliberately separate from Revelate's saved-game slot.
export const REVIEW_KEY = 'revelate_calculate_history_v1';
export const REVIEW_SAVE_KEY = 'revelate_calculate_checkpoint_v1';
export const REVIEW_VERSION = 1;
const copy = value => JSON.parse(JSON.stringify(value));
let idCounter = 0;
const uid = () => typeof window.crypto?.randomUUID === 'function' ? window.crypto.randomUUID() : `${Date.now()}-${++idCounter}`;

export function readHistory(storage = window.localStorage) {
  try {
    const data = JSON.parse(storage.getItem(REVIEW_KEY));
    return data?.version === REVIEW_VERSION && Array.isArray(data.games)
      ? data.games.filter(game => game.completedAt && Array.isArray(game.groups) && Number.isFinite(game.finalScore) && game.groups.every(g => Array.isArray(g.words))) : [];
  } catch { return []; }
}
export function createReview(openingScore = 0) {
  return { version:REVIEW_VERSION, id:uid(), startedAt:Date.now(), durationMs:0,
    activeSince:null, openingScore, coverage:openingScore ? 'since-save-resumed' : 'complete', groups:[] };
}
export function resumeReview(review) { review.activeSince = Date.now(); }
export function pauseReview(review) {
  if (review && review.activeSince !== null) {
    review.durationMs += Math.max(0, Date.now() - review.activeSince);
    review.activeSince = null;
  }
}
export function beginReveal(review, trigger) {
  const group = { id:`${review.id}:${review.groups.length + 1}`, ordinal:review.groups.length + 1,
    occurredAt:Date.now(), elapsedMs:review.durationMs + (review.activeSince === null ? 0 : Math.max(0, Date.now() - review.activeSince)),
    trigger, totalPoints:0, words:[] };
  review.groups.push(group);
  return group;
}

// Scoring supplies these credits; this module does not validate words or award points.
export function recordCredits(group, revealedWord, credits, context, bonusLetter) {
  const matchesAt = (word,start) => word.length + start <= revealedWord.length && [...word].every((letter,index) => {
    const revealed = revealedWord[start + index];
    return revealed === '?' || revealed === letter;
  });
  const primary = credits.find(credit => credit.word.length === revealedWord.length && matchesAt(credit.word,0))?.word || credits[0]?.word;
  const ordered = [...credits].sort((a,b) => Number(b.word === primary) - Number(a.word === primary));
  let hookStart = -1, hookEnd = -1, offset = 0;
  for (const cell of context.cells || []) {
    if (context.hookCell && cell.r === context.hookCell.row && cell.c === context.hookCell.col) {
      hookStart = offset; hookEnd = offset + cell.letter.length;
    }
    offset += cell.letter.length;
  }
  for (const credit of ordered) {
    let containsHook = false;
    for (let start = 0; start + credit.word.length <= revealedWord.length; start++) {
      if (!matchesAt(credit.word,start)) continue;
      if (hookStart >= 0 && start < hookEnd && start + credit.word.length > hookStart) containsHook = true;
    }
    const usesBonus = credit.word.includes(bonusLetter);
    group.words.push({ word:credit.word, length:credit.word.length, points:credit.points,
      type:credit.word === primary ? 'Reveal' : 'Subword', parentWord:revealedWord,
      direction:context.direction, source:containsHook ? 'Hook' : 'Cascade',
      bonusLetter:usesBonus ? bonusLetter : null, bonusPoints:usesBonus ? credit.points / 2 : 0 });
    group.totalPoints += credit.points;
  }
}

// Match a sidecar only to the exact persistent save. Never alter saved-game data.
export function saveSignature(snapshot) {
  const { grid,block,score,level,longWords,nextLetter,spawnCount,lastLongWordMilestone,recentLetters = [] } = snapshot;
  return JSON.stringify({grid,block,score,level,longWords,nextLetter,spawnCount,lastLongWordMilestone,recentLetters});
}
export function checkpointReview(review, snapshot, storage = window.localStorage) {
  try { storage.setItem(REVIEW_SAVE_KEY, JSON.stringify({version:REVIEW_VERSION,signature:saveSignature(snapshot),review})); return true; }
  catch { return false; }
}
export function restoreReview(snapshot, storage = window.localStorage) {
  try {
    const saved = JSON.parse(storage.getItem(REVIEW_SAVE_KEY));
    if (saved?.version === REVIEW_VERSION && saved.signature === saveSignature(snapshot) && Array.isArray(saved.review?.groups)) {
      const review = copy(saved.review);
      review.activeSince = null;
      // Continuing a completed game's earlier save is a new branch of play.
      if (readHistory(storage).some(game => game.id === review.id)) review.id = uid();
      return review;
    }
  } catch { /* An older save has no review metadata. */ }
  return { ...createReview(snapshot.score), coverage:'since-save-resumed' };
}
export function completeReview(review, {score,level,grid,block}) {
  pauseReview(review);
  const game = { ...copy(review), completedAt:Date.now(), finalScore:score, level,
    finalBoard:copy(grid), finalBlock:copy(block) };
  delete game.activeSince;
  game.accountedScore = game.openingScore + game.groups.reduce((sum,g) => sum + g.words.reduce((s,w) => s + w.points,0),0);
  game.reconciled = game.accountedScore === score && game.groups.every(g => g.totalPoints === g.words.reduce((s,w) => s+w.points,0));
  return game;
}
export function storeCompleted(game, storage = window.localStorage) {
  const games = readHistory(storage);
  if (!games.some(existing => existing.id === game.id)) games.push(game);
  try { storage.setItem(REVIEW_KEY, JSON.stringify({version:REVIEW_VERSION,games})); return {games,persisted:true}; }
  catch { return {games,persisted:false}; }
}
export function summarize(games) {
  const words = games.flatMap(g => g.groups.flatMap(group => group.words));
  const groups = games.flatMap(g => g.groups);
  const score = games.reduce((s,g)=>s+g.finalScore,0);
  const points = words.reduce((s,w)=>s+w.points,0);
  const bins = [3,4,5,6].map(length => {
    const found = words.filter(w=>length === 6 ? w.length >= 6 : w.length === length);
    return { label:length === 6 ? '6+' : String(length), count:found.length, points:found.reduce((s,w)=>s+w.points,0) };
  });
  const count = predicate => words.filter(predicate).length;
  const sum = predicate => words.filter(predicate).reduce((s,w)=>s+w.points,0);
  return { games:games.length, words:words.length, score, points, bins, groups:groups.length,
    averageScore:games.length ? score/games.length : 0,
    bestScore:Math.max(0,...games.map(g=>g.finalScore)),
    averageLevel:games.length ? games.reduce((s,g)=>s+g.level,0)/games.length : 0,
    averageLength:words.length ? words.reduce((s,w)=>s+w.length,0)/words.length : 0,
    reveals:count(w=>w.type==='Reveal'), subwords:count(w=>w.type==='Subword'),
    revealPoints:sum(w=>w.type==='Reveal'), subwordPoints:sum(w=>w.type==='Subword'),
    lr:count(w=>w.direction==='LR'), ud:count(w=>w.direction==='UD'),
    hook:count(w=>w.source==='Hook'), cascade:count(w=>w.source==='Cascade'),
    hookPoints:sum(w=>w.source==='Hook'), cascadePoints:sum(w=>w.source==='Cascade'),
    bonusUses:count(w=>w.bonusPoints>0), bonusPoints:words.reduce((s,w)=>s+w.bonusPoints,0),
    longest:words.filter(w=>w.type==='Reveal').sort((a,b)=>b.length-a.length||b.points-a.points)[0]?.word || '—',
    highest:groups.reduce((best,g)=>!best||g.totalPoints>best.totalPoints?g:best,null),
    partialGames:games.filter(g=>g.coverage!=='complete').length,
  };
}
